import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import API_BASE from "../config/api";
import {
  buildSavePromoScheduleBody,
  editPromoSchedule,
  formatScheduleDaysLabel,
  ianaTimezoneToGmt,
  isScheduleRouteMissingError,
  savePromoSchedule,
  SCHEDULE_DAYS,
} from "../utils/promoScheduleApi";
import { useAppNotify } from '../utils/errorHandler';
import BottomTabBar from "../components/BottomTabBar";
import {
  ArrowLeft,
  Calendar,
  Check,
  Image as ImageIcon,
  PlayCircle,
  ChevronDown,
} from "lucide-react";

const SCHEDULE_TEMPLATES = [
  {
    label: "All day",
    start: "00:00",
    end: "23:59",
    allDay: true,
  },
  {
    label: "Breakfast",
    start: "06:00",
    end: "11:00",
    allDay: false,
  },
  {
    label: "Lunch",
    start: "11:00",
    end: "15:00",
    allDay: false,
  },
  {
    label: "Dinner",
    start: "17:00",
    end: "22:00",
    allDay: false,
  },
  {
    label: "Morning",
    start: "06:00",
    end: "12:00",
    allDay: false,
  },
  {
    label: "Afternoon",
    start: "12:00",
    end: "17:00",
    allDay: false,
  },
  {
    label: "Evening",
    start: "17:00",
    end: "21:00",
    allDay: false,
  },
  {
    label: "Happy Hour",
    start: "16:00",
    end: "19:00",
    allDay: false,
  },
];

const TIMEZONES = [
  { label: "Eastern Time (ET)", value: "America/New_York" },
  { label: "Central Time (CT)", value: "America/Chicago" },
  { label: "Mountain Time (MT)", value: "America/Denver" },
  { label: "Pacific Time (PT)", value: "America/Los_Angeles" },
  { label: "Alaska Time", value: "America/Anchorage" },
  { label: "Hawaii Time", value: "Pacific/Honolulu" },
  { label: "Atlantic Time", value: "America/Halifax" },
  { label: "UTC / GMT", value: "UTC" },
  { label: "London (GMT/BST)", value: "Europe/London" },
  { label: "Paris / Berlin (CET)", value: "Europe/Paris" },
  { label: "India (IST)", value: "Asia/Kolkata" },
  { label: "Dubai (GST)", value: "Asia/Dubai" },
  { label: "Singapore / KL (SGT)", value: "Asia/Singapore" },
  { label: "Tokyo (JST)", value: "Asia/Tokyo" },
  { label: "Hong Kong (HKT)", value: "Asia/Hong_Kong" },
  { label: "Bangkok (ICT)", value: "Asia/Bangkok" },
  { label: "Sydney (AEST)", value: "Australia/Sydney" },
  { label: "Perth (AWST)", value: "Australia/Perth" },
  { label: "Nairobi (EAT)", value: "Africa/Nairobi" },
  { label: "Lagos (WAT)", value: "Africa/Lagos" },
  { label: "Johannesburg (SAST)", value: "Africa/Johannesburg" },
];

const getTimezoneDetails = (timeZone) => {
  const selectedTimezone = TIMEZONES.find((tz) => tz.value === timeZone);

  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "shortOffset",
    }).formatToParts(new Date());
    const gmtLabel =
      parts.find((part) => part.type === "timeZoneName")?.value || "";
    const offsetMatch = gmtLabel.match(/^GMT([+-])(\d{1,2})(?::(\d{2}))?$/);
    const gmtOffset = offsetMatch
      ? `${offsetMatch[1]}${offsetMatch[2].padStart(2, "0")}:${
          offsetMatch[3] || "00"
        }`
      : "";

    return {
      timezone: ianaTimezoneToGmt(timeZone),
      timezoneLabel: selectedTimezone?.label || timeZone,
      gmtLabel,
      gmtOffset,
    };
  } catch {
    return {
      timezone: ianaTimezoneToGmt(timeZone),
      timezoneLabel: selectedTimezone?.label || timeZone,
      gmtLabel: "",
      gmtOffset: "",
    };
  }
};

export default function CreateSchedulePage() {
  const navigate = useNavigate();
  const location = useLocation();
  const editScheduleId = location.state?.editScheduleId;

  const [modes, setModes] = useState([]);
  const [modesLoading, setModesLoading] = useState(true);
  const [selectedModeId, setSelectedModeId] = useState("");
  const [selectedModeName, setSelectedModeName] = useState("");
  const [promos, setPromos] = useState([]);
  const [loadingPromos, setLoadingPromos] = useState(false);
  const [modeDropdownOpen, setModeDropdownOpen] = useState(false);
  const [usedImageIds, setUsedImageIds] = useState(new Set());

  const { showNotify } = useAppNotify();


  const [step, setStep] = useState(1);

  const [gsName, setGsName] = useState("");
  const [gsStartTime, setGsStartTime] = useState("06:00");
  const [gsEndTime, setGsEndTime] = useState("23:00");
  const [gsTimezone, setGsTimezone] = useState(() => {
    try {
      return (
        Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York"
      );
    } catch {
      return "America/New_York";
    }
  });
  const [gsAllDay, setGsAllDay] = useState(false);
  const [gsDays, setGsDays] = useState([]);

  const [selectedIds, setSelectedIds] = useState([]);

  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);

  const allPromos = promos.filter((p) => !usedImageIds.has(String(p._id)));
  const hiddenCount = promos.length - allPromos.length;

  // Load the real schedule from the backend when editing.
  useEffect(() => {
    if (!editScheduleId) return;
    let cancelled = false;
    const loadScheduleForEdit = async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/getSchedule`, {
          params: { scheduleId: editScheduleId },
        });
        const sched = data?.data;
        if (!sched || cancelled) return;

        const entry = sched.schedule?.[0] || {};
        const imageIds = (sched.digitalAdsId || []).map((id) => String(id));

        setGsName(sched.scheduleName || "");
        setGsAllDay(Boolean(entry.allTime));
        setGsStartTime(entry.timeWindow?.[0]?.start || "06:00");
        setGsEndTime(entry.timeWindow?.[0]?.end || "23:00");
        setGsDays(entry.allDays ? [] : entry.days || []);
        setSelectedIds(imageIds);

        const matchedTz = TIMEZONES.find(
          (tz) => ianaTimezoneToGmt(tz.value) === entry.timezone,
        );
        if (matchedTz) {
          setGsTimezone(matchedTz.value);
        }
      } catch (err) {
        console.error("Failed to load schedule for edit", err);
        showNotify("Couldn't load the schedule. Please try again.", "error");
      }
    };
    loadScheduleForEdit();
    return () => {
      cancelled = true;
    };
  }, [editScheduleId]);

  // Resolve which screen mode the schedule's images belong to, so step 2
  // shows the right image picker with the existing selection intact.
  useEffect(() => {
    if (
      !editScheduleId ||
      modesLoading ||
      !modes.length ||
      selectedModeId ||
      !selectedIds.length
    ) {
      return;
    }
    let cancelled = false;
    const resolveMode = async () => {
      try {
        const results = await Promise.all(
          modes.map((mode) =>
            axios
              .get(`${API_BASE}/fetchPromosByMode/${mode._id}`)
              .then(({ data }) => ({
                mode,
                ids: (data?.data || []).map((p) => p._id),
              }))
              .catch(() => ({ mode, ids: [] })),
          ),
        );
        if (cancelled) return;
        const match = results.find((r) =>
          r.ids.some((id) => selectedIds.includes(id)),
        );
        if (match) {
          setSelectedModeId(match.mode._id);
          setSelectedModeName(match.mode.modeName);
        }
      } catch (err) {
        console.error("Failed to resolve schedule mode", err);
      }
    };
    resolveMode();
    return () => {
      cancelled = true;
    };
  }, [editScheduleId, modesLoading, modes, selectedIds, selectedModeId]);

  useEffect(() => {
    if (!localStorage.getItem("customer_id")) {
      navigate("/schedule-hub", { replace: true });
      return;
    }
    const fetchModes = async () => {
      setModesLoading(true);
      try {
        const customerId = localStorage.getItem("customer_id");
        const { data } = await axios.get(`${API_BASE}/getModes`, {
          params: { customer_id: customerId },
        });
        setModes(data?.data || []);
      } catch (err) {
        console.error(err);
        showNotify("Couldn't load screen modes. Please try again.", "error");
      } finally {
        setModesLoading(false);
      }
    };
    fetchModes();
  }, [navigate]);

  useEffect(() => {
    if (!selectedModeId) {
      setPromos([]);
      return;
    }
    const fetchPromos = async () => {
      setLoadingPromos(true);
      try {
        const { data } = await axios.get(
          `${API_BASE}/fetchPromosByMode/${selectedModeId}`,
        );
        setPromos(data?.data || []);
      } catch (err) {
        console.error(err);
        showNotify("Couldn't load images. Please try again.", "error");
      } finally {
        setLoadingPromos(false);
      }
    };
    fetchPromos();
  }, [selectedModeId]);

  useEffect(() => {
    if (!selectedModeId) {
      setUsedImageIds(new Set());
      return;
    }
    const fetchSchedules = async () => {
      try {
        const customerId = localStorage.getItem("customer_id");
        if (!customerId) return;
        const { data } = await axios.get(
          `${API_BASE}/getSchedules?customerId=${customerId}`
        );
        const schedules = data?.data || [];
        const collectedIds = [];
        schedules.forEach((s) => {
          if (String(s._id) !== String(editScheduleId)) {
            if (Array.isArray(s.digitalAdsId)) {
              collectedIds.push(...s.digitalAdsId);
            }
          }
        });
        setUsedImageIds(new Set(collectedIds.map(String)));
      } catch (err) {
        console.warn("Failed to fetch schedules for used images", err);
        setUsedImageIds(new Set());
      }
    };
    fetchSchedules();
  }, [selectedModeId, editScheduleId]);

  const applyGroupSchedule = async () => {
    const allSelected = [...selectedIds];
    if (!allSelected.length) {
      showNotify("No images selected", "warning");
      return;
    }
    setApplying(true);
    setApplyResult(null);
    const timezoneDetails = getTimezoneDetails(gsTimezone);
    const allDays = gsDays.length === 0;
    const body = buildSavePromoScheduleBody({
      scheduleName: gsName,
      imageIds: allSelected,
      allTime: gsAllDay,
      allDays,
      days: gsDays,
      startTime: gsAllDay ? "00:00" : gsStartTime,
      endTime: gsAllDay ? "23:59" : gsEndTime,
      timezone: timezoneDetails.timezone,
      scheduleId: editScheduleId,
    });

    try {
      const data = editScheduleId
        ? await editPromoSchedule(body)
        : await savePromoSchedule(body);

      if (data?.success) {
        setApplyResult({ success: allSelected.length, failed: 0 });
        showNotify(
          editScheduleId
            ? `Schedule updated for ${allSelected.length} image(s)`
            : `Schedule applied to ${allSelected.length} image(s)`,
          "success",
        );
      } else {
        setApplyResult({ success: 0, failed: allSelected.length });
        showNotify(data?.message || "Couldn't save the schedule. Please try again.", "error");
      }
    } catch (err) {
      console.error(err);
      const detail = isScheduleRouteMissingError(err)
        ? "Schedule API is not available on the server. Redeploy the Backend folder and restart the Node process (pm2 restart)."
        : err.response?.data?.message ||
          err.response?.statusText ||
          err.message;
      setApplyResult({ success: 0, failed: allSelected.length });
      showNotify(detail || "Couldn't save the schedule. Please try again.", "error");
    } finally {
      setApplying(false);
    }
  };

  const inputCls =
    "w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500";

  const ImageGrid = ({ promoList, selected, setSelected }) => (
    <>
      <div className="flex gap-2 mb-3 flex-wrap">
        <button
          type="button"
          onClick={() => setSelected(promoList.map((p) => p._id))}
          className="text-xs text-blue-600 font-semibold border border-blue-200 bg-blue-50 px-3 py-1 rounded-full"
        >
          Select all
        </button>
        <button
          type="button"
          onClick={() => setSelected([])}
          className="text-xs text-gray-600 font-semibold border border-gray-200 px-3 py-1 rounded-full"
        >
          Deselect all
        </button>
        <span className="ml-auto text-xs text-blue-600 font-semibold bg-blue-50 border border-blue-200 px-2 py-1 rounded-full">
          {selected.length} selected
        </span>
      </div>

      {promoList.length === 0 ? (
        <div className="py-10 flex flex-col items-center text-gray-400">
          <ImageIcon size={28} className="mb-2 opacity-30" />
          <p className="text-xs">No images in this screen</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-4">
          {promoList.map((promo) => {
            const sel = selected.includes(promo._id);
            return (
              <div
                key={promo._id}
                role="button"
                tabIndex={0}
                onClick={() =>
                  setSelected((prev) =>
                    prev.includes(promo._id)
                      ? prev.filter((id) => id !== promo._id)
                      : [...prev, promo._id],
                  )
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    setSelected((prev) =>
                      prev.includes(promo._id)
                        ? prev.filter((id) => id !== promo._id)
                        : [...prev, promo._id],
                    );
                  }
                }}
                className={`relative rounded-xl overflow-hidden cursor-pointer border-2 aspect-video flex items-center justify-center bg-gray-100 ${
                  sel ? "border-blue-500" : "border-transparent"
                }`}
              >
                {promo.S3link ? (
                  promo.file_type === "video" ||
                  promo.S3link.match(/\.(mp4|webm|ogg|mov)$/i) ? (
                    <div className="relative w-full h-full">
                      <video
                        src={promo.S3link}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <PlayCircle size={14} className="text-white" />
                      </div>
                    </div>
                  ) : (
                    <img
                      src={promo.S3link}
                      alt={promo.title}
                      className="w-full h-full object-cover"
                    />
                  )
                ) : (
                  <ImageIcon size={16} className="text-gray-300" />
                )}
                {sel && (
                  <div className="absolute top-1 right-1 w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center">
                    <Check size={10} className="text-white" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  const toggleDay = (val) =>
    setGsDays((prev) =>
      prev.includes(val) ? prev.filter((d) => d !== val) : [...prev, val],
    );

  return (
    <div className="min-h-screen bg-gray-100 flex flex-col pb-24">
      <div className="bg-white px-4 pt-5 pb-4 border-b border-gray-100 shadow-sm flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-gray-600 border border-gray-200 px-3 py-1.5 rounded-full"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <h1 className="text-lg font-extrabold text-gray-900 text-center truncate px-2">
          {editScheduleId ? "Edit Schedule" : "Create Schedule"}
        </h1>
        <div className="w-16 flex-shrink-0" />
      </div>

      <div className="flex-1 px-4 pt-5 max-w-lg w-full mx-auto">
        {step === 1 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <h2 className="text-base font-bold text-gray-900 mb-4">
              Schedule details
            </h2>

            <div className="mb-4">
              <label className="text-xs text-gray-600 font-medium mb-1 block">
                Screen mode
              </label>
              {modesLoading ? (
                <div className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-400 bg-gray-50">
                  Loading modes…
                </div>
              ) : (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setModeDropdownOpen((o) => !o)}
                    className={`w-full flex items-center justify-between border rounded-xl px-3 py-2.5 text-sm text-left outline-none ${
                      selectedModeId
                        ? "border-blue-300 bg-blue-50 text-blue-700 font-semibold"
                        : "border-gray-200 bg-white text-gray-400"
                    }`}
                  >
                    <span>{selectedModeName || "Choose a screen mode"}</span>
                    <ChevronDown
                      size={15}
                      className={`flex-shrink-0 transition-transform ${
                        modeDropdownOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {modeDropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 overflow-hidden">
                      {modes.length === 0 ? (
                        <div className="px-3 py-4 text-xs text-gray-400 text-center">
                          No modes found
                        </div>
                      ) : (
                        modes.map((mode) => (
                          <button
                            key={mode._id}
                            type="button"
                            onClick={() => {
                              setSelectedModeId(mode._id);
                              setSelectedModeName(mode.modeName);
                              setModeDropdownOpen(false);
                              setSelectedIds([]);
                            }}
                            className={`w-full text-left px-3 py-2.5 text-sm hover:bg-blue-50 transition-colors ${
                              selectedModeId === mode._id
                                ? "bg-blue-50 text-blue-700 font-semibold"
                                : "text-gray-900"
                            }`}
                          >
                            {mode.modeName}
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="mb-4">
              <label className="text-xs text-gray-600 font-medium mb-2 block">
                Quick templates
                <span className="text-gray-400 font-normal ml-1">
                  (tap to apply)
                </span>
              </label>
              <div className="flex gap-1.5 flex-wrap">
                {SCHEDULE_TEMPLATES.map((t) => (
                  <button
                    key={t.label}
                    type="button"
                    onClick={() => {
                      setGsAllDay(t.allDay);
                      setGsStartTime(t.start);
                      setGsEndTime(t.end);
                      setGsName(t.label);
                    }}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${
                      gsAllDay === t.allDay &&
                      gsStartTime === t.start &&
                      gsEndTime === t.end
                        ? "bg-blue-600 text-white border-blue-600"
                        : "bg-white text-gray-600 border-gray-200 hover:border-blue-300 hover:text-blue-600"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <label className="text-xs text-gray-600 font-medium mb-1 block">
                Schedule name
                <span className="text-gray-400 font-normal ml-1">
                  
                </span>
              </label>
              <input
                className={inputCls}
                placeholder="e.g. Breakfast, Happy Hour"
                value={gsName}
                onChange={(e) => setGsName(e.target.value)}
              />
            </div>

            <div
              role="button"
              tabIndex={0}
              onClick={() => setGsAllDay((v) => !v)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setGsAllDay((v) => !v);
              }}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer mb-3 ${
                gsAllDay
                  ? "border-blue-300 bg-blue-50"
                  : "border-gray-200 bg-white"
              }`}
            >
              <div
                className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                  gsAllDay
                    ? "border-blue-600 bg-blue-600"
                    : "border-gray-300 bg-white"
                }`}
              >
                {gsAllDay && <Check size={10} className="text-white" />}
              </div>
              <span
                className={`text-sm font-semibold ${
                  gsAllDay ? "text-blue-700" : "text-gray-700"
                }`}
              >
                All day (no time restriction)
              </span>
            </div>

            {!gsAllDay && (
              <div className="mb-4">
                <label className="text-xs text-gray-600 font-medium mb-2 block">
                  Time range
                </label>
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <label className="text-[10px] text-gray-400 mb-1 block">
                      Start time
                    </label>
                    <input
                      type="time"
                      value={gsStartTime}
                      onChange={(e) => setGsStartTime(e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div className="text-gray-400 text-sm font-medium mt-4">
                    to
                  </div>
                  <div className="flex-1">
                    <label className="text-[10px] text-gray-400 mb-1 block">
                      End time
                    </label>
                    <input
                      type="time"
                      value={gsEndTime}
                      onChange={(e) => setGsEndTime(e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <p className="text-[10px] text-gray-400 mt-1.5">
                  Promos will show between these times in your selected timezone.
                </p>
              </div>
            )}

            <div className="mb-4">
              <label className="text-xs text-gray-600 font-medium mb-1 block">
                Timezone
              </label>
              <div className="relative">
                <select
                  value={gsTimezone}
                  onChange={(e) => setGsTimezone(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-900 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 appearance-none bg-white pr-8 cursor-pointer"
                >
                  {TIMEZONES.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={14}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                />
              </div>
            </div>

            <p className="text-xs font-bold text-gray-900 mb-0.5">Days</p>
            <p className="text-xs text-gray-400 mb-2">
              Pick days or leave as all days.
            </p>

            <div
              role="button"
              tabIndex={0}
              onClick={() => setGsDays([])}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") setGsDays([]);
              }}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer mb-2 ${
                gsDays.length === 0
                  ? "border-blue-300 bg-blue-50"
                  : "border-gray-200 bg-white"
              }`}
            >
              <div
                className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                  gsDays.length === 0
                    ? "border-blue-600 bg-blue-600"
                    : "border-gray-300 bg-white"
                }`}
              >
                {gsDays.length === 0 && (
                  <Check size={10} className="text-white" />
                )}
              </div>
              <span
                className={`text-sm font-semibold ${
                  gsDays.length === 0 ? "text-blue-700" : "text-gray-700"
                }`}
              >
                All days
              </span>
            </div>

            <div className="grid grid-cols-4 sm:grid-cols-7 gap-1.5 mb-5">
              {SCHEDULE_DAYS.map((day) => {
                const sel = gsDays.includes(day.value);
                return (
                  <div
                    key={day.value}
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleDay(day.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ")
                        toggleDay(day.value);
                    }}
                    className={`flex flex-col items-center justify-center gap-1 py-2 rounded-xl border cursor-pointer ${
                      sel
                        ? "border-blue-300 bg-blue-50"
                        : "border-gray-200 bg-white"
                    }`}
                  >
                    <div
                      className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 ${
                        sel
                          ? "border-blue-600 bg-blue-600"
                          : "border-gray-300 bg-white"
                      }`}
                    >
                      {sel && <Check size={8} className="text-white" />}
                    </div>
                    <span
                      className={`text-[10px] font-medium ${
                        sel ? "text-blue-700" : "text-gray-600"
                      }`}
                    >
                      {day.label}
                    </span>
                  </div>
                );
              })}
            </div>

            <button
              type="button"
              onClick={() => setStep(2)}
              disabled={!gsName.trim() || !selectedModeId || loadingPromos}
              className="w-full bg-blue-600 text-white font-bold text-sm py-3.5 rounded-full disabled:opacity-40 flex items-center justify-center gap-2"
            >
              {loadingPromos ? "Loading promos…" : "Next — Select images"}
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <h2 className="text-base font-bold text-gray-900 mb-1">
              Select images
            </h2>
            <p className="text-xs text-gray-500 mb-4">
              Choose which promo images this schedule applies to.
            </p>

            {hiddenCount > 0 && (
              <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
                {hiddenCount} image{hiddenCount !== 1 ? 's' : ''} hidden — already used in another schedule.
              </p>
            )}

            <ImageGrid
              promoList={allPromos}
              selected={selectedIds}
              setSelected={setSelectedIds}
            />

            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="flex-1 border border-gray-200 text-gray-600 text-sm font-semibold py-3 rounded-full"
              >
                ← Back
              </button>
              <button
                type="button"
                onClick={() => setStep(3)}
                className="flex-1 bg-blue-600 text-white text-sm font-bold py-3 rounded-full"
              >
                Review →
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
            <h2 className="text-base font-bold text-gray-900 mb-4">
              Review schedule
            </h2>

            <div className="bg-gray-50 border border-gray-100 rounded-xl p-3.5 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar size={15} className="text-blue-600 flex-shrink-0" />
                <p className="text-sm font-bold text-gray-900 truncate">
                  {gsName}
                </p>
              </div>
              <p className="text-xs text-gray-400 mb-2 ml-5">
                Mode: {selectedModeName}
              </p>
              <div className="flex gap-1.5 flex-wrap">
                <span className="text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200 px-2.5 py-1 rounded-full">
                  {gsAllDay
                    ? "All day"
                    : `${gsStartTime} – ${gsEndTime}`}
                </span>
                <span className="text-xs font-semibold bg-purple-50 text-purple-700 border border-purple-200 px-2.5 py-1 rounded-full">
                  {TIMEZONES.find((tz) => tz.value === gsTimezone)?.label ||
                    gsTimezone}
                </span>
                <span className="text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-200 px-2.5 py-1 rounded-full">
                  {formatScheduleDaysLabel(gsDays)}
                </span>
                <span className="text-xs font-semibold bg-green-50 text-green-700 border border-green-200 px-2.5 py-1 rounded-full">
                  {selectedIds.length} images
                </span>
              </div>
            </div>

            <p className="text-xs font-bold text-gray-900 mb-2">
              Selected images
            </p>

            {selectedIds.length === 0 ? (
              <div className="py-6 flex flex-col items-center text-gray-400">
                <ImageIcon size={24} className="mb-2 opacity-30" />
                <p className="text-xs">No images selected</p>
              </div>
            ) : (
              <div className="space-y-2 mb-4">
                {promos
                  .filter((p) => selectedIds.includes(p._id))
                  .map((promo) => (
                    <div
                      key={promo._id}
                      className="flex items-center gap-3 bg-gray-50 rounded-xl px-3 py-2.5 border border-gray-100"
                    >
                      <div className="w-12 h-9 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0 flex items-center justify-center">
                        {promo.S3link ? (
                          promo.file_type === "video" ||
                          promo.S3link.match(/\.(mp4|webm|ogg|mov)$/i) ? (
                            <PlayCircle size={16} className="text-gray-400" />
                          ) : (
                            <img
                              src={promo.S3link}
                              alt={promo.title}
                              className="w-full h-full object-cover"
                            />
                          )
                        ) : (
                          <ImageIcon size={14} className="text-gray-300" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">
                          {promo.title || "Untitled"}
                        </p>
                        <p className="text-xs text-gray-400">
                          {promo.file_type === "video" ||
                          promo.S3link?.match(/\.(mp4|webm|ogg|mov)$/i)
                            ? "Video"
                            : "Image"}
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
            )}

            {applyResult && (
              <div
                className={`text-sm font-semibold px-4 py-3 rounded-xl border mb-4 ${
                  applyResult.failed === 0
                    ? "bg-green-50 border-green-200 text-green-700"
                    : "bg-yellow-50 border-yellow-200 text-yellow-700"
                }`}
              >
                {applyResult.failed === 0
                  ? editScheduleId
                    ? `✓ Schedule updated for all ${applyResult.success} image(s)`
                    : `✓ Schedule created for all ${applyResult.success} image(s)`
                  : `${applyResult.success} applied · ${applyResult.failed} failed`}
              </div>
            )}

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setStep(2)}
                disabled={applying}
                className="flex-1 border border-gray-200 text-gray-600 text-sm font-semibold py-3 rounded-full disabled:opacity-40"
              >
                ← Back
              </button>
              {applyResult?.failed === 0 ? (
                <button
                  type="button"
                  onClick={() => navigate("/schedule-hub")}
                  className="flex-1 bg-green-500 text-white text-sm font-bold py-3 rounded-full flex items-center justify-center gap-2"
                >
                  <Check size={15} /> Done
                </button>
              ) : (
                <button
                  type="button"
                  onClick={applyGroupSchedule}
                  disabled={applying || selectedIds.length === 0}
                  className="flex-1 bg-blue-600 text-white text-sm font-bold py-3 rounded-full flex items-center justify-center gap-2 disabled:opacity-40"
                >
                  {applying ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      {editScheduleId ? "Saving…" : "Creating…"}
                    </>
                  ) : (
                    <>
                      <Calendar size={15} />
                      {editScheduleId ? "Save Schedule" : "Create Schedule"}
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      <BottomTabBar />
    </div>
  );
}
